import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { PICOCard } from "../PICOCard/PicoCard";
import { QueryTermCard } from "../QuerytermCard/QueryTermCard";
import { fetchStudyDesigns } from "../../Utils/functions";
import { FilterContext } from "../../Context/FilterContext";
import { Frequency } from "../../Utils/constants";
import { PicoContext } from "../../Context/PicoContext";

const normaliseFilters = (incomingFilters = {}) => {
  const {
    selectedSex = "",
    age = "",
    fromDate,
    toDate,
    fromYear,
    toYear,
    abstracts = "",
    studyDesigns = [],
  } = incomingFilters;

  const fallbackFrom = fromYear ? `${fromYear}/01/01` : "";
  const fallbackTo = toYear ? `${toYear}/12/31` : "";

  return {
    selectedSex,
    age,
    fromDate: fromDate || fallbackFrom,
    toDate: toDate || fallbackTo,
    abstracts,
    studyDesigns,
  };
};

const EditScheduledJobModal = ({ job, onClose, onSave }) => {
  const { setFilters: setContextFilters } = useContext(FilterContext);
  const { pico, setPico } = useContext(PicoContext);
  const [question, setQuestion] = useState(job.question);
  const [query, setQuery] = useState(job.query || "");
  const [filters, setFilters] = useState(() => normaliseFilters(job.filters));
  const [searchMode, setSearchMode] = useState(job.searchMode || "public");
  const [frequency, setFrequency] = useState(job.frequency || Frequency.WEEKLY);
  const [studyDesignOptions, setStudyDesignOptions] = useState([]);
  const [loadingStudyDesigns, setLoadingStudyDesigns] = useState(false);
  const [error, setError] = useState({ from: "", to: "" });

  useEffect(() => {
    const loadStudyDesigns = async () => {
      try {
        setLoadingStudyDesigns(true);
        const data = await fetchStudyDesigns();
        const transformedData = data.map((design) => ({
          value: design.value,
          label: design.label,
        }));
        setStudyDesignOptions(transformedData);
      } catch (error) {
        console.error("Error loading study designs", error);
      } finally {
        setLoadingStudyDesigns(false);
      }
    };

    loadStudyDesigns();
    const initialFilters = normaliseFilters(job.filters);
    setPico(job.pico);
    setQuestion(job.question);
    setQuery(job.query || "");
    setFilters(initialFilters);
    setSearchMode(job.searchMode || "public");
    setFrequency(job.frequency || Frequency.QUARTERLY);
    setContextFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setContextFilters(filters);
  }, [filters, setContextFilters]);

  const handleSave = () => {
    if (validateDates()) {
      onSave({ ...job, question, pico, query, filters, searchMode, frequency });
    }
  };

  const validateDates = () => {
    let isValid = true;
    const errors = { from: "", to: "" };

    const DATE_REGEX = /^\d{4}\/\d{2}\/\d{2}$/;

    const parseDate = (value) => {
      if (!DATE_REGEX.test(value)) {
        return null;
      }

      const [yearStr, monthStr, dayStr] = value.split("/");
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      const date = new Date(year, month - 1, day);

      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return null;
      }

      return date;
    };

    const fromDateObj = filters.fromDate ? parseDate(filters.fromDate) : null;
    const toDateObj = filters.toDate ? parseDate(filters.toDate) : null;

    if (filters.fromDate && !fromDateObj) {
      errors.from = "Enter a valid date in YYYY/MM/DD format";
      isValid = false;
    }

    if (filters.toDate && !toDateObj) {
      errors.to = "Enter a valid date in YYYY/MM/DD format";
      isValid = false;
    }

    if (fromDateObj && toDateObj && fromDateObj > toDateObj) {
      errors.from = "From date cannot be after To date";
      isValid = false;
    }

    setError(errors);
    return isValid;
  };

  const handleStudyDesignChange = (value, checked) => {
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        studyDesigns: checked
          ? [...prev.studyDesigns, value]
          : prev.studyDesigns.filter((id) => id !== value),
      };

      return nextFilters;
    });
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Scheduled Search</DialogTitle>
      <DialogContent>
        <TextField
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          fullWidth
          margin="normal"
        />
        <Box my={2}>
          <PICOCard />
        </Box>
        <Box my={2}>
          <QueryTermCard query={query} setQuery={setQuery} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6">Search Filters</Typography>

        <Stack direction="row" spacing={2} sx={{ my: 2 }}>
          <TextField
            label="From Date"
            placeholder="YYYY/MM/DD"
            variant="outlined"
            value={filters.fromDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
            }
            error={!!error.from}
            helperText={error.from}
            size="small"
          />
          <TextField
            label="To Date"
            placeholder="YYYY/MM/DD"
            variant="outlined"
            value={filters.toDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, toDate: e.target.value }))
            }
            error={!!error.to}
            helperText={error.to}
            size="small"
          />
        </Stack>

        <FormControl size="small" variant="outlined" fullWidth sx={{ my: 2 }}>
          <Select
            value={searchMode}
            onChange={(e) => setSearchMode(e.target.value)}
            displayEmpty
          >
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" fullWidth sx={{ my: 2 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            label="Frequency"
          >
            {Object.values(Frequency).map((freq) => (
              <MenuItem key={freq} value={freq}>
                {freq}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h6" component="h3" sx={{ mt: 2 }}>
          Methodology
        </Typography>
        {loadingStudyDesigns ? (
          <Typography variant="body2" color="text.secondary">
            Loading study designs...
          </Typography>
        ) : (
          <FormGroup>
            {studyDesignOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={filters.studyDesigns.includes(option.value)}
                    onChange={(e) =>
                      handleStudyDesignChange(option.value, e.target.checked)
                    }
                    value={option.value}
                    size="small"
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditScheduledJobModal;
